import { Dialog, DialogActions, DialogContent } from "@mui/material"
import type { FC } from "react"
import { FormProvider } from "react-hook-form"

import { useGetInvoice } from "../../hooks/useGetInvoice"
import { useInvoiceForm } from "../../hooks/useInvoiceForm"
import type { InvoiceFormDialogMode } from "../../types/dialog"
import { DuplicateWarningDialog } from "../DuplicateWarningDialog"
import { InvoiceForm } from "../InvoiceForm/InvoiceForm"
import { InvoiceReviewDialog } from "../InvoiceReviewDialog"

import { Button } from "@/components/common/Button"
import { CancelDialog, ConfirmationDialog } from "@/components/common/Modals"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import { EntityLockBanner, EntityLockConflictDialog, useEntityLock } from "@/entityLock"
import type { Invoice } from "@/graphql/codegen/graphql"
import { EntityLockEntityType } from "@/graphql/codegen/graphql"

interface InvoiceFormDialogProps {
  onClose: () => void
  onSuccess?: () => void
  onRefresh?: () => void
  invoice?: Invoice
  mode: InvoiceFormDialogMode
}

const TITLE_MAP: Record<InvoiceFormDialogMode, string> = {
  create: "Add Invoice",
  edit: "Edit Invoice",
  "edit-resubmittable": "Edit Invoice",
  view: "View Invoice",
}

export const InvoiceFormDialog: FC<InvoiceFormDialogProps> = ({ onClose, onSuccess, onRefresh, invoice, mode }) => {
  const isEditMode = mode === "edit" || mode === "edit-resubmittable"

  const { holder, isReadOnly, markLost } = useEntityLock({
    entityType: EntityLockEntityType.Invoice,
    entityId: invoice?.invoiceId,
    enabled: isEditMode,
  })

  const {
    methods,
    canSubmit,
    isProcessing,
    attachmentStatuses,
    attachmentProgress,
    isUploadingAttachments,
    isCancelConfirmationOpen,
    isDuplicateWarningOpen,
    isSaveConfirmationOpen,
    isResubmitConfirmationOpen,
    isLockConflictOpen,
    lockConflictMessage,
    duplicates,
    reviewingInvoiceId,
    selectedVendorOption,
    handleSubmit,
    handleSaveConfirm,
    handleSaveAndResubmit,
    handleSaveAndResubmitConfirm,
    handleCancel,
    handleDuplicateWarningCancel,
    handleDuplicateWarningBypass,
    handleReviewOpen,
    handleReviewClose,
    handleLockConflictClose,
    handleLockConflictRefresh,
    setIsCancelConfirmationOpen,
    setIsSaveConfirmationOpen,
    setIsResubmitConfirmationOpen,
    setSelectedVendorOption,
  } = useInvoiceForm({ onClose, onSuccess, onRefresh, invoice, mode, onLockConflict: markLost })

  const { invoice: reviewingInvoice } = useGetInvoice({ invoiceId: reviewingInvoiceId })

  const isView = mode === "view"
  const isCreate = mode === "create"
  const isResubmittable = mode === "edit-resubmittable"

  const isBannerVisible = isEditMode && isReadOnly

  if (isLockConflictOpen && lockConflictMessage) {
    return (
      <EntityLockConflictDialog
        message={lockConflictMessage}
        onClose={handleLockConflictClose}
        onRefresh={handleLockConflictRefresh}
      />
    )
  }

  if (isCancelConfirmationOpen) {
    return <CancelDialog onClose={() => setIsCancelConfirmationOpen(false)} onConfirm={onClose} />
  }

  if (isDuplicateWarningOpen) {
    return (
      <>
        <DuplicateWarningDialog
          duplicates={duplicates}
          onCancel={handleDuplicateWarningCancel}
          onBypass={handleDuplicateWarningBypass}
          onReview={handleReviewOpen}
        />
        {reviewingInvoice && <InvoiceReviewDialog invoice={reviewingInvoice} onClose={handleReviewClose} />}
      </>
    )
  }

  if (isSaveConfirmationOpen) {
    return (
      <ConfirmationDialog
        title={
          isCreate ? "Are you sure you want to save this invoice?" : "Are you sure you want to save these changes?"
        }
        content={
          isCreate ? "Once saved, the invoice will be submitted for processing." : "The invoice will be updated."
        }
        closeLabel='Go Back'
        confirmLabel='Yes, Save'
        onClose={() => setIsSaveConfirmationOpen(false)}
        onConfirm={handleSaveConfirm}
      />
    )
  }

  if (isResubmitConfirmationOpen) {
    return (
      <ConfirmationDialog
        title='Are you sure you want to save and resubmit?'
        content='This will save the invoice and move it back to Pending Approval.'
        closeLabel='Go Back'
        confirmLabel='Yes, Save and Resubmit'
        onClose={() => setIsResubmitConfirmationOpen(false)}
        onConfirm={handleSaveAndResubmitConfirm}
      />
    )
  }

  return (
    <Dialog open onClose={handleCancel} maxWidth='md' fullWidth data-testid='invoice.form-dialog'>
      <DialogHeader title={TITLE_MAP[mode]} onClose={handleCancel} />
      <FormProvider {...methods}>
        <DialogContent className='flex flex-col gap-6 pt-4'>
          {isBannerVisible && <EntityLockBanner holder={holder} />}
          <InvoiceForm
            mode={mode}
            invoice={invoice}
            readOnly={isView || isBannerVisible}
            selectedVendorOption={selectedVendorOption}
            onSelectedVendorOptionChange={setSelectedVendorOption}
            attachmentStatuses={attachmentStatuses}
            attachmentProgress={attachmentProgress}
            isUploadingAttachments={isUploadingAttachments}
          />
        </DialogContent>
        <DialogActions className='px-6 pb-6'>
          <Button
            appearance='outlined'
            onClick={handleCancel}
            disabled={isProcessing}
            data-testid='invoice.form-dialog.cancel-button'
          >
            {isView ? "Close" : "Cancel"}
          </Button>
          {!isView && (
            <Button
              appearance='contained'
              onClick={handleSubmit}
              disabled={!canSubmit || isBannerVisible}
              data-testid='invoice.form-dialog.submit-button'
            >
              {isCreate ? "Save" : "Save Changes"}
            </Button>
          )}
          {isResubmittable && (
            <Button
              appearance='contained'
              onClick={handleSaveAndResubmit}
              disabled={!canSubmit || isBannerVisible}
              data-testid='invoice.form-dialog.save-and-resubmit-button'
            >
              Save and Resubmit
            </Button>
          )}
        </DialogActions>
      </FormProvider>
    </Dialog>
  )
}
